import { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

// Enregistrer le service worker
export function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
      try {
        // D'abord, désinscrire tous les anciens SW pour éviter les conflits
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          // Vérifier si c'est un ancien SW problématique
          if (registration.active && !registration.active.scriptURL.includes('service-worker.js')) {
            console.log('Unregistering old service worker:', registration.scope);
            await registration.unregister();
          }
        }
        
        // Enregistrer notre SW
        const registration = await navigator.serviceWorker.register('/service-worker.js');
        console.log('SW registered:', registration.scope);
        
        // Vérifier les mises à jour
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          newWorker?.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('New SW version available, activating...');
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
        
        // Forcer l'update check
        registration.update().catch(() => {});
        
      } catch (error) {
        console.log('SW registration failed:', error);
        
        // En cas d'erreur, désinscrire tous les SW pour repartir de zéro
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const reg of registrations) {
            await reg.unregister();
          }
          console.log('All service workers unregistered due to error');
        } catch (e) {
          console.log('Could not unregister service workers:', e);
        }
      }
    });
  }
}

// Hook pour détecter si l'app peut être installée
export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Vérifier si déjà installé (standalone ou fullscreen)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    
    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Écouter l'événement beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Écouter l'installation
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) return false;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setIsInstallable(false);
    
    return outcome === 'accepted';
  };

  return { isInstallable, isInstalled, promptInstall };
}

// Bannière d'installation PWA
export function PWAInstallBanner() {
  const { isInstallable, promptInstall } = useInstallPrompt();
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Vérifier si déjà refusé
    const wasDismissed = localStorage.getItem('pwa-install-dismissed');
    if (wasDismissed) {
      const dismissedAt = new Date(wasDismissed);
      const daysSince = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60 * 24);
      // Re-montrer après 7 jours
      if (daysSince < 7) {
        setDismissed(true);
        return;
      }
    }

    // Afficher après 30 secondes ou 2 visites
    const visitCount = parseInt(localStorage.getItem('pwa-visit-count') || '0', 10) + 1;
    localStorage.setItem('pwa-visit-count', visitCount.toString());

    if (isInstallable && !dismissed) {
      if (visitCount >= 2) {
        // 2+ visites: afficher immédiatement
        setShowBanner(true);
      } else {
        // 1ère visite: attendre 30s
        const timer = setTimeout(() => {
          setShowBanner(true);
        }, 30000);
        return () => clearTimeout(timer);
      }
    }
  }, [isInstallable, dismissed]);

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  };

  const handleInstall = async () => {
    const installed = await promptInstall();
    if (installed) {
      setShowBanner(false);
    }
  };

  if (!showBanner || !isInstallable) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 bg-mutagim-dark border-t border-gray-800 p-4 z-50 animate-slide-up"
      data-testid="pwa-install-banner"
    >
      <div className="max-w-lg mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-mutagim-red/20 p-2 rounded-lg">
            <Smartphone className="w-6 h-6 text-mutagim-red" />
          </div>
          <div>
            <p className="text-white font-medium text-sm">
              Installer Mutagim.shop
            </p>
            <p className="text-gray-400 text-xs">
              Accès rapide depuis votre écran d'accueil
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDismiss}
            className="text-gray-400 hover:text-white"
            data-testid="pwa-dismiss-btn"
          >
            <X className="w-5 h-5" />
          </Button>
          <Button
            onClick={handleInstall}
            className="bg-mutagim-red hover:bg-mutagim-red/90 text-white"
            data-testid="pwa-install-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Installer
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PWAInstallBanner;
